import {
  useCallback, useEffect, useRef, useState,
} from 'react'
import {
  Link,
} from 'react-router-dom'
import {
  Check, Copy, Gift, Loader2, Send,
} from 'lucide-react'
import {
  referralService,
} from '../services/api'
import {
  useReferralLink,
} from '../hooks/useReferralLink'
import {
  useReferralStatus,
} from '../hooks/useReferralStatus'
import {
  useReferralInvites,
} from '../hooks/useReferralInvites'
import {
  ReferralProgress,
} from './ReferralProgress'
import type {
  SuggestedContact,
} from '../client'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  AVATAR_TINTS,
  STATUS_PILL,
  initials,
} from './referralShared'

/**
 * "Who else needs Catch?" — a compact referral strip lower down the
 * Integrations page (below the integrations grid). Surfaces the user's stable referral link (Copy),
 * assistant-ranked contact suggestions (one-tap Send), and a free-form invite.
 * Friends get 50% off their first month; the invite email is sent from the
 * user's assistant. Degrades gracefully when suggestions/link are missing.
 */
export function ReferralStrip() {
  // Shared with ReferralLinkModal — React Query dedups the fetch when both
  // are on the page (deep-linked arrivals open the modal alongside the strip).
  const { data: link } = useReferralLink()
  // Tiered referrer-reward programme (free months per qualifying referral):
  // the footer shows the reward + live progress.
  const { status: referralStatus } = useReferralStatus()
  const [suggestions, setSuggestions] = useState<SuggestedContact[]>([])
  const stripRef = useRef<HTMLDivElement | null>(null)
  const inViewTracked = useRef(false)
  const removeFromSuggestions = useCallback((email: string) => {
    setSuggestions((prev) => prev.filter((c) => c.email.toLowerCase() !== email))
  }, [])
  const {
    invited,
    sending,
    inviteEmail,
    setInviteEmail,
    inviteSending,
    copied,
    error,
    sendContactInvite,
    sendFreeformInvite,
    copyLink,
  } = useReferralInvites({
    contactInviteEvent: OnboardingEvents.REFERRAL_SUGGESTION_SENT,
    removeFromList: removeFromSuggestions,
  })

  useEffect(() => {
    referralService.getSuggestions().then(setSuggestions).catch(() => {})
  }, [])

  // Render impression — fires on mount (the strip is in the DOM). Kept for
  // continuity, but it does NOT mean the user saw the strip now that it sits
  // below the fold; use REFERRAL_STRIP_IN_VIEW for the true "saw it" signal.
  useEffect(() => {
    trackEvent(OnboardingEvents.REFERRAL_STRIP_VIEWED)
  }, [])

  // True impression — fires once when the strip actually scrolls into view. The
  // strip lives below the integrations grid, so a mount fire would log
  // below-the-fold renders and inflate the viewed→clicked rate.
  useEffect(() => {
    if (inViewTracked.current) return
    const el = stripRef.current
    if (!el) return

    const fire = (): void => {
      if (inViewTracked.current) return
      inViewTracked.current = true
      trackEvent(OnboardingEvents.REFERRAL_STRIP_IN_VIEW)
    }

    if (typeof IntersectionObserver === 'undefined') {
      fire()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fire()
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // The reward footer shows the user's live hotel-credit progress (we wait
  // for the status to load, to avoid a flash).
  const referralFooter = referralStatus
    ? <ReferralProgress status={referralStatus} source="strip" />
    : (
      <div className="flex items-center justify-center px-4 py-3 border-t border-[#F3D9D9] bg-[#FCEEEE]">
        <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />
      </div>
    )
  return (
    <div ref={stripRef} className="mt-6 rounded-xl border border-[#F3D9D9] bg-white shadow-soft overflow-hidden">
      <div className="px-4 py-3 bg-[#FCEEEE] border-b border-[#F3D9D9]">
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2 sm:flex-1 min-w-0">
            <span className="w-6 h-6 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center shrink-0">
              <Gift className="w-3.5 h-3.5" />
            </span>
            <span className="text-sm font-bold text-gray-900">
              Who else needs Catch?
            </span>
            <Link
              to="/refer"
              onClick={() => trackEvent(OnboardingEvents.REFERRAL_PAGE_OPENED_FROM_STRIP)}
              className="hidden sm:inline text-xs font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap"
            >
              See your whole network →
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void sendFreeformInvite() }}
              placeholder="Invite by email…"
              className="flex-1 min-w-0 sm:flex-none sm:w-44 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
            <button
              onClick={() => void sendFreeformInvite()}
              disabled={inviteSending}
              aria-label="Send invite"
              className="shrink-0 inline-flex items-center gap-1 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg shadow-soft transition-[background-color,box-shadow,transform] active:scale-[0.96] disabled:opacity-60"
            >
              {inviteSending
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Send className="w-3.5 h-3.5" />}
              Send
            </button>
            {link && (
              <button
                onClick={() => { if (link) void copyLink(link.referral_url) }}
                title="Copy link"
                aria-label="Copy invite link"
                className="shrink-0 inline-flex items-center gap-1.5 text-primary-600 hover:bg-primary-50 text-xs font-medium px-2 py-1.5 rounded-lg transition-colors"
              >
                {copied
                  ? <Check className="w-3.5 h-3.5" />
                  : <Copy className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy link'}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {suggestions.length > 0 && (
        <div className="p-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {suggestions.map((contact, i) => {
            const isSending = sending.has(contact.email)
            return (
              <div
                key={contact.email}
                title={contact.email}
                className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg pl-1.5 pr-1 py-1"
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 ${AVATAR_TINTS[i % AVATAR_TINTS.length]}`}>
                  {initials(contact.name, contact.email)}
                </div>
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="text-xs font-semibold text-gray-900 truncate">{contact.name}</div>
                  <div className="text-[10px] text-gray-500 truncate">{contact.role ?? contact.email}</div>
                </div>
                <button
                  onClick={() => void sendContactInvite(contact)}
                  disabled={isSending}
                  title={`Send invite to ${contact.name}`}
                  aria-label={`Send invite to ${contact.name}`}
                  className="w-7 h-7 rounded-md bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white flex items-center justify-center shrink-0 shadow-soft transition-[background-color,box-shadow,transform] active:scale-[0.96] disabled:opacity-60"
                >
                  {isSending
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Send className="w-3.5 h-3.5" />}
                </button>
              </div>
            )
          })}
        </div>
      )}

      {invited.length > 0 && (
        <div className="px-2 pb-2">
          <div className="px-1.5 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Invited
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {invited.map((contact) => {
              const pill = STATUS_PILL[contact.status]
              const PillIcon = pill.icon
              return (
                <div
                  key={contact.email}
                  title={contact.email}
                  className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg pl-1.5 pr-2 py-1"
                >
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 bg-gray-100 text-gray-500">
                    {initials(contact.name, contact.email)}
                  </div>
                  <div className="min-w-0 flex-1 leading-tight">
                    <div className="text-xs font-semibold text-gray-700 truncate">{contact.name ?? contact.email}</div>
                    <div className="text-[10px] text-gray-400 truncate">{contact.email}</div>
                  </div>
                  <span
                    title={pill.title}
                    className={`inline-flex items-center gap-1 shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${pill.className}`}
                  >
                    <PillIcon className="w-3 h-3" />
                    {pill.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {error && (
        <p className="px-4 pb-2.5 pt-0 text-[11px] text-red-600">{error}</p>
      )}

      {referralFooter}
    </div>
  )
}
